import { Fragment, useState } from 'react';

import {
  faChevronDown,
  faEdit,
  faTimes
} from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { startOfToday } from 'date-fns';
import { DutyHourTypeDynFlag } from 'generated';
import { useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';

import { WorkAgreementDetails } from 'components/types';

import {
  getDefaultDHT,
  getReferenceBenefit,
  getReferenceBenefitOrDefault,
  getReferenceDate
} from 'components/utils';
import {
  formatByLocale,
  fromISOString,
  getDiffInMinutes,
  getHoursMinutes,
  TimePeriod
} from 'utils/date';

import { queries } from 'RestAPIs/rapini';

import { isEmployeePortalState } from './atoms';

import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';

import { FontAwesomeSvgIcon } from 'components/shared';
import DutyDialog, {
  createDuty,
  Duty,
  getDutyDuration
} from 'components/shared/DutyDialog/DutyDialog';

import WorkDayStyles from './WorkDayStyles';
import dayjs from 'dayjs';

type DutyTypePreset = 'fullday' | 'morning' | 'afternoon' | 'custom';

type Props = {
  date: Date;
  duties?: Duty[];
  isEditMode: boolean;
  workAgreements: WorkAgreementDetails[];
  startDate: Date | null;
  endDate: Date | null;
  updateDuties: (duties: Duty[]) => void;
  isInEmployeeCardDialog?: boolean;
};

const addHoursAndMinutes = (
  timePeriod: TimePeriod,
  hoursToAdd = 0,
  minutesToAdd = 0
): TimePeriod => {
  const today = startOfToday();
  const [initialHours, initialMinutes] = timePeriod.split(':');
  today.setHours(parseInt(initialHours, 10), parseInt(initialMinutes, 10));

  return dayjs(today)
    .add(hoursToAdd, 'hours')
    .add(minutesToAdd, 'minutes')
    .format('HH:mm') as TimePeriod;
};

export default function WorkDay({
  date,
  duties = [],
  isEditMode,
  workAgreements,
  startDate,
  endDate,
  updateDuties,
  isInEmployeeCardDialog
}: Props) {
  const { t } = useTranslation('common');
  const { publicPlanUnits, organisationPreferences } = useSelector(
    (state) => state
  );
  const { settings: orgSettings } = organisationPreferences;
  const [showDutyDialog, setShowDutyDialog] = useState(false);
  const [dutyToEdit, setDutyToEdit] = useState<Duty | null>(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isEmployeePortal = useRecoilValue(isEmployeePortalState);
  const open = Boolean(menuAnchorEl);
  const hasDuties = Boolean(duties.length);
  const { data: dutyHourTypes } = queries.useGetDutyHourTypes();

  const findDutyHourType = (id?: number) => {
    return dutyHourTypes?.find(({ dutyHourTypeId }) => dutyHourTypeId === id);
  };

  const breakDuration = parseInt(
    orgSettings['workPattern.defaultBreakDuration'],
    10
  );
  const morningStartTime = orgSettings['workPattern.defaultMorningStartTime'];
  const afternoonStartTime =
    orgSettings['workPattern.defaultAfternoonStartTime'];
  const morningEndTime = addHoursAndMinutes(
    afternoonStartTime,
    0,
    -breakDuration
  );
  const fullDayEndTime = addHoursAndMinutes(morningStartTime, 8, breakDuration);

  const addDutyOptions: { type: DutyTypePreset; label: string }[] = [
    {
      type: 'fullday',
      label: `${t('fullDay')} (${morningStartTime} - ${fullDayEndTime})`
    },
    {
      type: 'morning',
      label: `${t('morning')} (${morningStartTime} - ${morningEndTime})`
    },
    {
      type: 'afternoon',
      label: `${t('afternoon')} (${afternoonStartTime} - ${fullDayEndTime})`
    },
    { type: 'custom', label: t('custom') }
  ];

  const closeMenu = () => {
    setMenuAnchorEl(null);
  };

  const createDefaultDuty = (timePeriods: TimePeriod[]) => {
    const selectedWorkAgreement =
      workAgreements.find(
        (it) => getReferenceBenefit(it, startDate, endDate) != null
      ) || workAgreements[0];
    return createDuty({
      timePeriods,
      dutyHourType: getDefaultDHT(dutyHourTypes),
      workAgreement: selectedWorkAgreement,
      planUnit:
        publicPlanUnits.find(
          ({ id }) =>
            id ===
            getReferenceBenefitOrDefault(
              selectedWorkAgreement,
              startDate,
              endDate
            )?.brId
        ) ?? publicPlanUnits[0]
    });
  };

  const createDutyByType = (type: DutyTypePreset) => {
    const presets: {
      [K in DutyTypePreset]: TimePeriod[];
    } = {
      fullday: [
        morningStartTime,
        morningEndTime,
        afternoonStartTime,
        fullDayEndTime
      ],
      morning: [morningStartTime, morningEndTime],
      afternoon: [afternoonStartTime, fullDayEndTime],
      custom: [
        morningStartTime,
        morningEndTime,
        afternoonStartTime,
        fullDayEndTime
      ]
    };
    return createDefaultDuty(presets[type]);
  };

  const handleMenuOption = (type: DutyTypePreset) => {
    closeMenu();
    if (type === 'custom') {
      setDutyToEdit(createDutyByType(type));
      setShowDutyDialog(true);
    } else {
      const newDuties = [createDutyByType(type)];
      updateDuties(newDuties);
    }
  };

  const removeDuty = (dutyToRemove: Duty) => {
    const newDuties = duties.filter((duty) => duty !== dutyToRemove);
    updateDuties(newDuties);
  };

  const onDialogDutySaved = (duty: Duty) => {
    const isExistingDuty = dutyToEdit && duties.includes(dutyToEdit);
    let newDuties: Duty[] = [];

    if (isExistingDuty) {
      const indexOfDuty = duties.indexOf(dutyToEdit);

      newDuties = [
        ...duties.slice(0, indexOfDuty),
        duty,
        ...duties.slice(indexOfDuty + 1)
      ];
    } else {
      newDuties = [...duties, duty];
    }
    updateDuties(newDuties);
    setDutyToEdit(null);
  };

  const addAnotherDuty = () => {
    const lastDuty = duties[duties.length - 1];
    if (lastDuty) {
      const { endTime } = lastDuty.periods[lastDuty.periods.length - 1];
      const endTimeDate = fromISOString(endTime);
      setDutyToEdit(
        createDefaultDuty([
          dayjs(endTimeDate).format('HH:mm') as TimePeriod,
          dayjs(endTimeDate).format('HH:mm') as TimePeriod
        ])
      );
      setShowDutyDialog(true);
    }
  };

  const dutyHourTypeFlags: DutyHourTypeDynFlag[] = isEmployeePortal
    ? ['EXS_DUTY_TIMES_CREATE']
    : [];

  return (
    <WorkDayStyles
      className={classNames('work-day d-flex v-center between', {
        selected: hasDuties
      })}
    >
      <div className="d-flex v-center">
        <div className="day-name semibold">{formatByLocale(date, 'EEEE')}</div>
      </div>
      <div className="grow-1">
        {duties.length === 0 && (
          <div className="d-flex v-center between">
            <i>{t('noDuty')}</i>
            {isEditMode && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => setMenuAnchorEl(e.currentTarget)}
                  endIcon={
                    <FontAwesomeSvgIcon icon={faChevronDown} fontSize="small" />
                  }
                >
                  {t('addDuty')}
                </Button>
                <Menu
                  anchorEl={menuAnchorEl}
                  open={open}
                  onClose={closeMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                >
                  {addDutyOptions.map(({ type, label }) => (
                    <MenuItem key={type} onClick={() => handleMenuOption(type)}>
                      {label}
                    </MenuItem>
                  ))}
                </Menu>
              </>
            )}
          </div>
        )}
        {duties.length > 0 && (
          <>
            <div className="duties">
              {duties.map((duty) => (
                <div className="duty d-flex v-center" key={duty.id}>
                  <div className="details d-flex v-center">
                    <div className="time-info">
                      <div className="header">
                        <span>
                          {getHoursMinutes(duty.periods[0].startTime)}
                        </span>
                        {' - '}
                        <span>
                          {getHoursMinutes(
                            duty.periods[duty.periods.length - 1].endTime
                          )}
                        </span>
                      </div>
                      <div className="duration">
                        {t('duration')}
                        {': '}
                        {getDutyDuration(duty)}
                      </div>
                    </div>
                    <div className="wa-info">
                      <div>
                        {t('workAgreement')} {duty.workAgreement?.nr}
                      </div>
                      <div>{duty.planUnit?.name}</div>
                    </div>
                  </div>
                  <div className="periods">
                    {duty.periods.map((period, i) => (
                      <Fragment
                        key={`${duty.id}-${period.dutyHourTypeId}-${i}`}
                      >
                        <div className="period">
                          <span>{getHoursMinutes(period.startTime)}</span>
                          {' - '}
                          <span>{getHoursMinutes(period.endTime)}</span>{' '}
                          <span>
                            {findDutyHourType(period.dutyHourTypeId)
                              ?.description || ''}
                          </span>
                        </div>
                        {i < duty.periods.length - 1 && (
                          <div className="period">
                            {getDiffInMinutes(
                              duty.periods[i + 1].startTime,
                              period.endTime
                            )}{' '}
                            m {t('pauseBreak')}
                          </div>
                        )}
                      </Fragment>
                    ))}
                  </div>
                  {isEditMode && (
                    <div className="actions d-flex end">
                      <Tooltip title={t('editDuty') || ''}>
                        <IconButton
                          onClick={() => {
                            setDutyToEdit(duty);
                            setShowDutyDialog(true);
                          }}
                          color="primary"
                          className="edit-duty"
                        >
                          <FontAwesomeSvgIcon icon={faEdit} fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={t('removeDuty') || ''}>
                        <IconButton
                          onClick={() => removeDuty(duty)}
                          color="primary"
                          className="edit-duty"
                        >
                          <FontAwesomeSvgIcon icon={faTimes} fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {isEditMode && (
              <Button
                variant="text"
                size="small"
                className="add-duty-btn custom-btn"
                onClick={addAnotherDuty}
              >
                {t('addAnotherDuty')}
              </Button>
            )}
          </>
        )}
      </div>
      {showDutyDialog && (
        <DutyDialog
          duty={dutyToEdit}
          onSave={onDialogDutySaved}
          onHidden={() => setShowDutyDialog(false)}
          usePublicPlanUnits
          dutyHourTypeDynFlags={dutyHourTypeFlags}
          workAgreements={workAgreements}
          startDate={startDate}
          endDate={endDate}
          isInEmployeeCardDialog={isInEmployeeCardDialog}
          referenceDate={getReferenceDate(startDate, endDate)}
          checkRestrictedAuthorization
        />
      )}
    </WorkDayStyles>
  );
}
